// src/profile/ProfileView.jsx
import { PencilSquareIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import { AlertTriangle, Check } from "lucide-react"; // Import for Toast/Modal
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { connectSocket, getSocket } from "../socket";

export default function ProfileView() {
  const { matriid } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);

  // Modal and Toast state
  const [modal, setModal] = useState({ show: false, title: "", message: "", onConfirm: null, isDestructive: false });
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [processing, setProcessing] = useState(false);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const API = process.env.REACT_APP_API_BASE || "";
  const FETCH_API = `${API}/api/auth/searchByMatriID`;

  // interest
  const [interest, setInterest] = useState(null);
  const [, setIsBlocked] = useState(false);
  const [blockedByMe, setBlockedByMe] = useState(false);

  const logged = (() => {
    try {
      return JSON.parse(localStorage.getItem("userData"));
    } catch {
      return null;
    }
  })();


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  const loggedId =
    (logged?.MatriID || logged?.matid || "").toString().trim() || null;
  const viewedId =
    (user?.MatriID || user?.matid || "").toString().trim() || null;

  useEffect(() => {
    connectSocket();
    const socket = getSocket();
    const handleInterestResponse = (payload) => {
      try {
        if (payload?.interest) setInterest(payload.interest);
      } catch (e) {
        console.error("handleInterestResponse error", e);
      }
    };
    socket?.on("interest_response", handleInterestResponse);
    socket?.on("interest_update", handleInterestResponse);
    return () => {
      socket?.off("interest_response", handleInterestResponse);
      socket?.off("interest_update", handleInterestResponse);
    };
  }, []);

  useEffect(() => {
    const fetchStatus = async () => {
      if (!loggedId || !viewedId) return;
      try {
        const res = await axios.get(`${API}/api/auth/interest/status`, {
          params: { from: loggedId, to: viewedId },
        });
        if (res.data?.success) {
          setInterest(res.data.interest);
          setIsBlocked(res.data.isBlocked);
          setBlockedByMe(res.data.blockedByMe);
        }
      } catch (e) {
        console.error("fetch interest status error", e);
      }
    };
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const isOwner = !!loggedId && loggedId === viewedId;
  const contactVisible =
    isOwner || (interest && interest.status === "accepted");

  const sendInterest = async () => {
    if (!loggedId) return showToast("Log in to send interest", "error");
    if (!viewedId) return showToast("Invalid profile", "error");
    try {
      const res = await axios.post(`${API}/api/auth/interest/send`, {
        fromMatriID: loggedId,
        toMatriID: viewedId,
      });
      if (res.data?.success) {
        setInterest(res.data.interest);
        showToast("Interest sent.", "success");
      } else if (res.data?.isBlocked) {
        showToast("You cannot connect with this profile.", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to send interest", "error");
    }
  };

  const blockUser = () => {
    setModal({
      show: true,
      title: "Block User",
      message: "Are you sure you want to block this user? You will not be able to see them again.",
      isDestructive: true,
      onConfirm: async () => {
        setProcessing(true);
        try {
          const res = await axios.post(`${API}/api/auth/interest/block`, {
            blockerMatriID: loggedId,
            blockedMatriID: viewedId,
          });
          if (res.data?.success) {
            setIsBlocked(true);
            setBlockedByMe(true);
            setInterest(null); // Clear interest state immediately
            showToast("User blocked and interest removed.", "success");
          }
        } catch (err) {
          console.error(err);
          showToast("Failed to block user", "error");
        } finally {
          setProcessing(false);
          setModal({ show: false, title: "", message: "", onConfirm: null });
        }
      }
    });
  };

  const unblockUser = () => {
    setModal({
      show: true,
      title: "Unblock User",
      message: "Are you sure you want to unblock this user?",
      isDestructive: false,
      onConfirm: async () => {
        setProcessing(true);
        try {
          const res = await axios.post(`${API}/api/auth/interest/unblock`, {
            blockerMatriID: loggedId,
            blockedMatriID: viewedId,
          });
          if (res.data?.success) {
            setIsBlocked(false);
            setBlockedByMe(false);
            showToast("User unblocked.", "success");
            // Refresh interest status to ensure UI is in sync
            const statusRes = await axios.get(`${API}/api/auth/interest/status`, {
              params: { from: loggedId, to: viewedId },
            });
            if (statusRes.data?.success) {
              setInterest(statusRes.data.interest);
            }
          }
        } catch (err) {
          console.error(err);
          showToast("Failed to unblock user", "error");
        } finally {
          setProcessing(false);
          setModal({ show: false, title: "", message: "", onConfirm: null });
        }
      }
    });
  };

  useEffect(() => {
    if (!matriid) {
      setError("No MatriID supplied");
      setLoading(false);
      return;
    }
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const loggedUser = JSON.parse(localStorage.getItem("userData") || "{}");
        const loggedPlan = loggedUser.Plan || "basic";
        const res = await axios.get(FETCH_API, { params: { matriid, loggedPlan } });
        if (res.data?.success && res.data.user) setUser(res.data.user);
        else setError("Profile not found");
      } catch (err) {
        console.error("fetch profile error", err);
        setError("Failed to fetch profile");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [matriid]); // eslint-disable-line

  const formatDOB = (dob) => {
    if (!dob) return { day: "-", month: "-", year: "-" };
    try {
      const d = new Date(dob);
      const day = d.getDate();
      const month = d.toLocaleString(undefined, { month: "long" });
      const year = d.getFullYear();
      return { day, month, year };
    } catch {
      return { day: "-", month: "-", year: "-" };
    }
  };

  const parseTimeOfBirth = (tob) => {
    if (!tob) return { hh: "-", mm: "-", ss: "-", ap: "-" };
    const cleaned = tob.replace(/\s+/g, "");
    const parts = cleaned.split(":");
    if (parts.length >= 3) {
      const [hh, mm, rest] = parts;
      let ss = "00";
      let ap = "AM";
      if (
        rest.toUpperCase().includes("AM") ||
        rest.toUpperCase().includes("PM")
      ) {
        ss = rest.replace(/[^0-9]/g, "") || "00";
        ap = rest.replace(/[^APMapm]/g, "").toUpperCase() || ap;
      }
      else {
        ss = rest;
      }
      return { hh, mm, ss, ap };
    }
    return { hh: "-", mm: "-", ss: "-", ap: "-" };
  };

  const timeOfBirth = (tob) =>
    [tob.hh, tob.mm, tob.ss].filter(Boolean).join(":") +
    (tob.ap ? ` ${tob.ap}` : "");

  const photoSrc = (u) => u?.PhotoURL || `${API}/gallery/nophoto.jpg`;

  const InfoRow = ({ label, value }) => (
    <div className="flex flex-col">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="font-medium text-[#333333] dark:text-gray-200">
        {value ?? "-"}
      </span>
    </div>
  );

  // if (loading) return <div className="p-8">Loading...</div>;
  if (loading)
    return (
      <div className="min-h-screen bg-cover bg-center bg-fixed p-6 font-display">
        <div className="max-w-[1140px] mx-auto mt-20 space-y-10 animate-pulse">
          {/* ⭐ Banner skeleton */}
          <div className="w-full h-[220px] bg-gray-200 rounded-xl"></div>

          {/* ⭐ Avatar + Title skeleton */}
          <div className="flex items-center gap-5 -mt-16 px-4">
            <div className="w-32 h-32 bg-gray-200 rounded-full border-4 border-white"></div>

            <div className="flex flex-col gap-3 mt-16">
              <div className="h-6 w-48 bg-gray-200 rounded"></div>
              <div className="h-4 w-64 bg-gray-200 rounded"></div>
            </div>
          </div>

          {/* ⭐ Section Card Skeleton Generator */}
          {[
            "About Me",
            "Basic Details",
            "Horoscope Details",
            "Contact Details",
            "Education & Professional",
            "Basic & Lifestyle",
            "Family Details",
            "Partner Preference",
          ].map((title, index) => (
            <div
              key={index}
              className="bg-white dark:bg-[#221019] rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
            >
              {/* Title skeleton */}
              <div className="h-6 w-40 bg-gray-200 rounded mb-4"></div>

              {/* Section rows skeleton */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="flex flex-col gap-2">
                    <div className="h-4 w-28 bg-gray-200 rounded"></div>
                    <div className="h-5 w-40 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>

              {/* If Family / Partner has extra long text field */}
              {(title === "Family Details" ||
                title === "Partner Preference") && (
                <div className="mt-6">
                  <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
                  <div className="h-5 w-full bg-gray-200 rounded"></div>
                </div>
              )}
            </div>
          ))}

          {/* ⭐ Special Horoscope Rasi & Navamsa boxes */}
          <div className="flex justify-center gap-12 my-10">
            <div className="w-[300px] h-[300px] bg-gray-200 rounded"></div>
            <div className="w-[300px] h-[300px] bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="p-8">
        <div className="text-red-600 mb-4">{error}</div>
        <button
          className="px-4 py-2 bg-gray-200 rounded"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>
    );
  if (!user) return <div className="p-8">No profile data</div>;

  
  const rasi = [
    user.g1,
    user.g2,
    user.g3,
    user.g4,
    user.g5,
    user.g6,
    user.g7,
    user.g8,
    user.g9,
    user.g10,
    user.g11,
    user.g12,
  ];

  const navamsa = [
    user.a1,
    user.a2,
    user.a3,
    user.a4,
    user.a5,
    user.a6,
    user.a7,
    user.a8,
    user.a9,
    user.a10,
    user.a11,
    user.a12,
  ];

  const dobParts = formatDOB(user.DOB || user.Regdate);
  const tob = parseTimeOfBirth(user.TOB);
  const primary = "#ec1380";

  /* ------------------ COMPATIBILITY HELPERS ------------------ */

  const isEmptyValue = (v) => {
    return (
      v === null ||
      v === undefined ||
      v === "" ||
      v === "-" ||
      (typeof v === "string" && v.trim() === "")
    );
  };

  // Compare strings (case-insensitive)
 const cmp = (pref, actual) => {
   // If preference is Any or empty → OK
   if (isEmptyValue(pref) || pref === "Any") return true;

   // If partner value missing → OK
   if (isEmptyValue(actual)) return true;

   return (
     pref.toString().trim().toLowerCase() ===
     actual.toString().trim().toLowerCase()
   );
 };


  // Multi-value comparison (Education, Occupation, Complexion)
const multiMatch = (pref, actual) => {
  if (isEmptyValue(pref) || pref === "Any") return true;
  if (isEmptyValue(actual)) return true;

  const prefArr = pref.split(",").map((v) => v.trim().toLowerCase());
  const actualStr = actual.trim().toLowerCase();

  return prefArr.some((p) => actualStr === p || actualStr.includes(p));
};


  // Convert "5Ft 7 Inch" → total inches
  // const heightToInches = (h) => {
  //   if (!h) return 0;
  //   const match = h.match(/(\d+)\s*Ft\s*(\d*)\s*Inch/i);
  //   if (!match) return 0;

  //   const ft = parseInt(match[1] || 0);
  //   const inch = parseInt(match[2] || 0);

  //   return ft * 12 + inch;
  // };

  const heightToInches = (h) => {
    if (!h) return null;

    const str = h.toLowerCase().replace(/\s+/g, "");

    const ftMatch = str.match(/(\d+)(ft|feet)/);
    const inMatch = str.match(/(\d+)(in|inch|inches)/);

    const ft = ftMatch ? parseInt(ftMatch[1]) : 0;
    const inch = inMatch ? parseInt(inMatch[1]) : 0;

    return ft * 12 + inch;
  };




const heightMatch = (minH, maxH, actual) => {
  if (isEmptyValue(minH) && isEmptyValue(maxH)) return true;
  if (isEmptyValue(actual)) return true;

  const actualIn = heightToInches(actual);
  const minIn = heightToInches(minH);
  const maxIn = heightToInches(maxH);

  if (!actualIn || !minIn || !maxIn) return true;

  return actualIn >= minIn && actualIn <= maxIn;
};





  /* ------------------ COMPATIBILITY RULES ------------------ */

  const displayValue = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === "-" ||
      (typeof value === "string" && value.trim() === "")
    ) {
      return "-";
    }
    return value;
  };


  const viewer = logged || {}; // logged-in user
  const profile = user || {}; // viewed user

  const compatibility = [
    {
      label: "Looking For",
      icon: "👤",
      yourValue: viewer.Looking,
      partnerValue: profile.Maritalstatus,
      pass: multiMatch(viewer.Looking, profile.Maritalstatus),
    },

    {
      label: "Age",
      icon: "📅",
      yourValue: displayValue(
        viewer.PE_FromAge && viewer.PE_ToAge
          ? `${viewer.PE_FromAge} To ${viewer.PE_ToAge}`
          : "-"
      ),
      partnerValue: displayValue(profile.Age),

      pass:
        // ✅ IF YOUR AGE PREFERENCE EMPTY → ✔️
        !viewer.PE_FromAge || !viewer.PE_ToAge || !profile.Age
          ? true
          : profile.Age >= viewer.PE_FromAge && profile.Age <= viewer.PE_ToAge,
    },

    {
      label: "Height",
      icon: "📏",
      yourValue: displayValue(
        viewer.PE_from_Height && viewer.PE_to_Height
          ? `${viewer.PE_from_Height} To ${viewer.PE_to_Height}`
          : "-"
      ),
      partnerValue: displayValue(profile.HeightText || profile.Height),

      pass:
        // ✅ IF YOUR HEIGHT PREF EMPTY → ✔️
        !viewer.PE_from_Height ||
        !viewer.PE_to_Height ||
        !(profile.HeightText || profile.Height)
          ? true
          : heightMatch(
              viewer.PE_from_Height,
              viewer.PE_to_Height,
              profile.HeightText || profile.Height
            ),
    },

    {
      label: "Complexion",
      icon: "🧑",
      yourValue: displayValue(viewer.PE_Complexion),
      partnerValue: displayValue(profile.Complexion),

      pass:
        isEmptyValue(viewer.PE_Complexion) || // no preference → OK
        isEmptyValue(profile.Complexion) || // partner missing → OK
        multiMatch(viewer.PE_Complexion, profile.Complexion),
    },

    {
      label: "Religion",
      icon: "✴️",
      yourValue: viewer.PE_Religion,
      partnerValue: profile.Religion,
      pass: cmp(viewer.PE_Religion, profile.Religion),
    },
    {
      label: "Caste",
      icon: "✔️",
      yourValue: viewer.PE_Caste,
      partnerValue: profile.Caste,
      pass: cmp(viewer.PE_Caste, profile.Caste),
    },
    {
      label: "Occupation",
      icon: "💼",
      yourValue: viewer.PE_Occupation,
      partnerValue: profile.Occupation,
      pass: multiMatch(viewer.PE_Occupation, profile.Occupation),
    },
    {
      label: "Education",
      icon: "🎓",
      yourValue: viewer.PE_Education,
      partnerValue: profile.Education,
      pass: multiMatch(viewer.PE_Education, profile.Education),
    },
    {
      label: "Country Living In",
      icon: "🚩",
      yourValue: viewer.PE_Countrylivingin,
      partnerValue: profile.Country,
      pass: cmp(viewer.PE_Countrylivingin, profile.Country),
    },
    {
      label: "State",
      icon: "🎯",
      yourValue: viewer.PE_State,
      partnerValue: profile.State,
      pass: cmp(viewer.PE_State, profile.State),
    },
    {
      label: "Resident Status",
      icon: "🏠",
      yourValue: viewer.PE_Residentstatus,
      partnerValue: profile.Residencystatus,
      pass: cmp(viewer.PE_Residentstatus, profile.Residencystatus),
    },
  ];

  const total = compatibility.length;
  const score = compatibility.filter((c) => c.pass).length;


  const showDash = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === "-" ||
      (typeof value === "string" && value.trim() === "")
    ) {
      return "-";
    }
    return value;
  };


  return (
    <div
      className="min-h-screen bg-cover bg-center bg-fixed p-6 font-display bg-gradient-to-b from-[#0f0c29] via-[#302b63] to-[#a17c5b]"
      f
    >
      {/* Toast Notification */}
      {toast.show && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg text-white transform transition-all duration-300 ease-in-out ${
            toast.type === "error" ? "bg-red-500" : "bg-green-500"
          }`}
        >
          <div className="flex items-center gap-2">
            {toast.type === "error" ? (
              <AlertTriangle size={20} />
            ) : (
              <Check size={20} />
            )}
            <span className="font-medium">{toast.message}</span>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {modal.show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all scale-100">
            <div className="p-6">
              <h3
                className={`text-xl font-bold mb-2 ${
                  modal.isDestructive ? "text-red-600" : "text-gray-800"
                }`}
              >
                {modal.title}
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {modal.message}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() =>
                    setModal({
                      show: false,
                      title: "",
                      message: "",
                      onConfirm: null,
                    })
                  }
                  className="px-5 py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={modal.onConfirm}
                  disabled={processing}
                  className={`px-5 py-2.5 rounded-lg text-white font-medium shadow-sm transition-colors flex items-center gap-2 ${
                    modal.isDestructive
                      ? "bg-red-500 hover:bg-red-600"
                      : "bg-green-500 hover:bg-green-600"
                  }`}
                >
                  {processing ? "Processing..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1140px] mx-auto">
        {/* Top card (same as ProfilePage) */}
        <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-sm overflow-hidden mt-20">
          <div className="relative">
            {/* Banner */}
            <div
              className="w-full bg-center bg-no-repeat bg-cover flex flex-col justify-end min-h-[220px]"
              style={{
                backgroundImage: `url(${
                  user.banner ||
                  user.bannerUrl ||
                  "https://lh3.googleusercontent.com/aida-public/AB6AXuCBZ4EFVao53pjOuSJDv5KALO-pbijH2rwxoJdNtEKYBlL3ZejOkXrSBt_D-eA-2sXzOZm2k77yDIi77LnXcWBzv491MC2tSL3H5MzMF89YHqdU1Pc8BRsMeuuKev3VlSXZaD2wMUdYU659o1JqMbgCjc8PBajptkzCTcH-9qHvamovnVGY6KP2XyV4H2mciLgwUI3SAaef9LmApUzaF9NdPg6Zzg6QV0O0cVoMp2FZ0jOvSmA-0Bl825eC8VzI7U39lVLNEzkbT5xE"
                })`,
              }}
              data-alt="Profile banner"
            ></div>

            {/* Avatar overlap */}
            <div className="absolute -bottom-16 left-8">
              <div className="relative">
                <div
                  onClick={() => setShowImagePreview(true)}
                  className="bg-no-repeat bg-cover rounded-full border-4 border-card-light dark:border-card-dark cursor-pointer hover:scale-105 transition-transform"
                  style={{
                    width: 128,
                    height: 128,
                    backgroundImage: `url(${
                      photoSrc(user) && photoSrc(user) !== ""
                        ? photoSrc(user)
                        : "/nophoto.jpg"
                    })`,
                    backgroundPosition: "top center",
                  }}
                />
              </div>
            </div>
          </div>

          {/* -------- PHOTO GALLERY -------- */}
          {/* {Array.isArray(user.gallery) && user.gallery.length > 0 && (
            <section className="bg-white dark:bg-[#221019] rounded-xl p-6 mt-12">
              <h2 className="text-xl font-bold mb-4">Photos</h2>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {user.gallery.map((img, index) => (
                  <div
                    key={index}
                    className="border rounded-lg overflow-hidden bg-gray-100"
                  >
                    <img
                      src={img}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-40 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            </section>
          )} */}

          {/* -------- PHOTO GALLERY -------- */}
          {Array.isArray(user.gallery) && user.gallery.length > 0 && (
            <section className="bg-white dark:bg-[#221019] rounded-xl p-6 mt-12">
              <h2 className="text-xl font-bold mb-4">Photos</h2>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {user.gallery.map((img, index) => (
                  <div
                    key={index}
                    className="relative border rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition group"
                  >
                    <img
                      src={img}
                      alt={`Gallery ${index + 1}`}
                      className="w-full aspect-[3/4] object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Header content: name, id, actions */}
          <div className="pt-20 px-8 pb-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-text-light-primary dark:text-text-dark-primary text-2xl font-bold leading-tight tracking-[-0.015em]">
                    {user.Name || "-"}
                  </p>
                  {user.isVerified && (
                    <span className="material-symbols-outlined text-blue-500 text-xl">
                      verified
                    </span>
                  )}
                </div>
                <p className="text-text-light-secondary dark:text-text-dark-secondary text-sm font-normal leading-normal mt-1">
                  {user.MatriID || user.matid || "-"} &nbsp;|&nbsp;{" "}
                  {user.Religion || "-"} &nbsp;|&nbsp;{" "}
                  {user.Maritalstatus || "-"} &nbsp;|&nbsp; {user.Age || "-"}
                </p>
              </div>

              <div className="flex w-full md:w-auto gap-3">
                {isOwner ? (
                  <button
                    onClick={() => navigate("/profile/edit")}
                    className="flex min-w-[84px] max-w-[480px] items-center justify-center rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold flex-1 gap-2"
                    style={{ backgroundColor: primary }}
                  >
                    <span className="material-symbols-outlined text-base">
                      edit
                    </span>
                    <span className="truncate">Edit Profile</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={sendInterest}
                      disabled={
                        interest?.status === "pending" ||
                        interest?.status === "accepted"
                      }
                      className="flex min-w-[84px] max-w-[480px] items-center justify-center rounded-lg h-10 px-4 text-white text-sm font-bold flex-1"
                      style={{
                        backgroundColor: primary,
                        opacity:
                          interest?.status === "pending" ||
                          interest?.status === "accepted"
                            ? 0.8
                            : 1,
                      }}
                    >
                      {interest?.status === "accepted"
                        ? "Interest Accepted"
                        : interest?.status === "pending"
                          ? "Interest Sent"
                          : "Send Interest"}
                    </button>

                    {blockedByMe ? (
                      <button
                        onClick={unblockUser}
                        className="flex min-w-[84px] max-w-[480px] items-center justify-center rounded-lg h-10 px-4 bg-yellow-500 text-white text-sm font-bold flex-1 gap-2 hover:bg-yellow-600 transition"
                      >
                        <span className="material-symbols-outlined text-base">
                          undo
                        </span>
                        <span className="truncate">Unblock</span>
                      </button>
                    ) : (
                      <button
                        onClick={blockUser}
                        className="flex min-w-[84px] max-w-[480px] items-center justify-center rounded-lg h-10 px-4 bg-red-500 text-white text-sm font-bold flex-1 gap-2 hover:bg-red-600 transition"
                      >
                        <span className="material-symbols-outlined text-base">
                          block
                        </span>
                        <span className="truncate">Block</span>
                      </button>
                    )}

                    <button
                      onClick={() =>
                        navigate(`/chat/${user.MatriID || user.matid}`)
                      }
                      className="flex min-w-[84px] max-w-[480px] items-center justify-center rounded-lg h-10 px-4 text-white text-sm font-bold flex-1 gap-2"
                      style={{ backgroundColor: "#22c55e" }}
                    >
                      <span className="material-symbols-outlined text-base">
                        chat
                      </span>
                      <span className="truncate">Message</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* About Me */}
        <section className="bg-white dark:bg-[#221019] rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6 mt-10">
          <h2 className="text-xl font-bold mb-2" style={{ color: "#181114" }}>
            About Me
          </h2>
          <p className="text-base leading-relaxed text-[#333333] dark:text-gray-300">
            {user.aboutus || "-"}
          </p>
        </section>

        {/* Basic Details */}
        <section className="mb-6 relative">
          <div className="bg-white dark:bg-[#221019] rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold">Basic Details</h2>
              {isOwner && (
                <button
                  onClick={() => navigate("/profile/edit#basic")}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <PencilSquareIcon className="w-5 h-5" />
                  Edit
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
              <InfoRow label="Name" value={showDash(user.Name)} />

              <InfoRow
                label="Matri ID"
                value={showDash(user.MatriID || user.matid)}
              />

              <InfoRow
                label="Profile Created By"
                value={showDash(user.Profilecreatedby)}
              />

              <InfoRow label="Gender" value={showDash(user.Gender)} />

              <InfoRow
                label="Date of Birth (Day)"
                value={showDash(dobParts.day)}
              />
              <InfoRow label="Month" value={showDash(dobParts.month)} />
              <InfoRow label="Year" value={showDash(dobParts.year)} />

              <InfoRow
                label="Marital Status"
                value={showDash(user.Maritalstatus || user.maritalstatus)}
              />

              <InfoRow label="Religion" value={showDash(user.Religion)} />
              <InfoRow label="Caste" value={showDash(user.Caste)} />

              <InfoRow
                label="Subcaste"
                value={showDash(user.Subcaste || user.sub_caste)}
              />

              <InfoRow
                label="Mobile Number"
                value={
                  contactVisible ? (
                    showDash(user.Mobile || user.Phone)
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <span className="blur-sm select-none pointer-events-none">
                        +91-xxxx-xxx-xxx
                      </span>
                    </span>
                  )
                }
              />
            </div>
          </div>
        </section>

        {/* Horoscope Details */}
        <section className="mb-6 relative">
          <div className="bg-white dark:bg-[#221019] rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold">Horoscope Details</h2>
              {isOwner && (
                <button
                  onClick={() => navigate("/profile/edit#horoscope")}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <PencilSquareIcon className="w-5 h-5" />
                  Edit
                </button>
              )}
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
              <InfoRow
                label="Moon Sign"
                value={user.Moonsign || user.moonsign || "-"}
              />
              <InfoRow label="Star" value={user.Star || user.star || "-"} />
              <InfoRow label="Lagnam" value={user.Lagnam || "-"} />
              <InfoRow label="Gothra" value={user.Gothram || "-"} />
              <InfoRow label="Mangalik" value={user.Manglik || "-"} />

              <InfoRow label="Shani" value={user.Shani || user.shani || "-"} />
              <InfoRow
                label="Place of Shani"
                value={user.shaniplace || user.place || "-"}
              />

              <InfoRow label="Horoscope Match" value={user.Horosmatch || "-"} />
              <InfoRow label="Kootam" value={user.Kootam || "-"} />

              <InfoRow
                label="Parigarasevai"
                value={user.parigarasevai || "-"}
              />
              <InfoRow label="Sevai" value={user.Sevai || "-"} />
              <InfoRow label="Raghu" value={user.Raghu || "-"} />
              <InfoRow label="Keethu" value={user.Keethu || "-"} />
              <InfoRow label="Sutham" value={user.Sutham || "-"} />

              <InfoRow
                label="Place of Birth"
                value={
                  user.POB || user.PlaceOfBirth || user.place_of_birth || "-"
                }
              />
              <InfoRow
                label="Country / Place"
                value={user.POC || user.Country || user.country || "-"}
              />

              <InfoRow label="Time of Birth" value={timeOfBirth(tob) || "-"} />

              {/* Thesai Details */}
              <InfoRow label="Thesai Planet" value={user.ThesaiPlanet || "-"} />
              <InfoRow
                label="Thesai Years"
                value={
                  user.ThesaiYears !== undefined && user.ThesaiYears !== null
                    ? user.ThesaiYears
                    : "-"
                }
              />
              <InfoRow label="Thesai Months" value={user.ThesaiMonths || "-"} />
              <InfoRow label="Thesai Days" value={user.ThesaiDays || "-"} />

              {/* RASI + NAVAMSA Charts */}
              <div className="col-span-1 sm:col-span-2 lg:col-span-3 flex flex-wrap justify-center gap-12 my-6">
                <SouthChart title="இராசி" data={rasi} />
                <SouthChart title="நவாம்சம்" data={navamsa} />
              </div>

              {/* Horoscope Image / PDF */}
              <div className="col-span-1 sm:col-span-2 lg:col-span-3 mt-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Horoscope (Image / PDF)
                </span>

                {/* <div className="mt-2">
                  {user.horoscope ? (
                    <img
                      src={
                        user.horoscope.startsWith("http")
                          ? user.horoscope
                          : `${API}/gallery/${user.horoscope}`
                      }
                      alt="horoscope"
                      className="w-full max-w-[300px] object-contain rounded border border-gray-200 dark:border-gray-700"
                    />
                  ) : (
                    <div className="text-gray-400 dark:text-gray-500">
                      No horoscope uploaded
                    </div>
                  )}
                </div> */}

                <div className="mt-2">
                  {user.HoroscopeURL ? (
                    <img
                      src={user.HoroscopeURL}
                      alt="Horoscope"
                      className="w-full max-w-[300px] object-contain rounded border border-gray-200 dark:border-gray-700"
                    />
                  ) : (
                    <div className="text-gray-400 dark:text-gray-500">
                      No horoscope uploaded
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Education & Professional */}
        <section className="mb-6 relative">
          <div className="bg-white dark:bg-[#221019] rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold">Education & Professional</h2>
              {isOwner && (
                <button
                  onClick={() => navigate("/profile/edit#education")}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <PencilSquareIcon className="w-5 h-5" />
                  Edit
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
              <div>
                <div className="text-sm text-gray-500">
                  Education Qualification
                </div>
                <div className="font-medium">{showDash(user.Education)}</div>
              </div>

              <div>
                <div className="text-sm text-gray-500">Education Details</div>
                <div className="font-medium">
                  {showDash(user.EducationDetails)}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500">Occupation</div>
                <div className="font-medium">{showDash(user.Occupation)}</div>
              </div>

              <div>
                <div className="text-sm text-gray-500">Occupation Details</div>
                <div className="font-medium">
                  {showDash(user.occu_details || user.OccupationDetails)}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500">Monthly Income</div>
                <div className="font-medium">
                  {user.Annualincome
                    ? `${showDash(user.income_in) || "Rs"} ${user.Annualincome}`
                    : "-"}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500">Employed In</div>
                <div className="font-medium">{showDash(user.Employedin)}</div>
              </div>

              <div>
                <div className="text-sm text-gray-500">Working Hours</div>
                <div className="font-medium">
                  {showDash(user.working_hours)}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500">
                  Working Location / City
                </div>
                <div className="font-medium">
                  {showDash(user.workinglocation)}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500">Company Name</div>
                <div className="font-medium">{showDash(user.company_name)}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Basic & Lifestyle */}
        <section className="mb-6 relative">
          <div className="bg-white dark:bg-[#221019] rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold">Basic & Lifestyle</h2>
              {isOwner && (
                <button
                  onClick={() => navigate("/profile/edit#lifestyle")}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <PencilSquareIcon className="w-5 h-5" />
                  Edit
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
              <InfoRow
                label="Height"
                value={user.Height ? `${user.Height}` : "-"}
              />
              <InfoRow label="Height" value={showDash(user.Height)} />
              <InfoRow label="Weight" value={showDash(user.Weight)} />
              <InfoRow label="Blood Group" value={showDash(user.BloodGroup)} />
              <InfoRow label="Complexion" value={showDash(user.Complexion)} />
              <InfoRow label="Body Type" value={showDash(user.Bodytype)} />
              <InfoRow label="Diet" value={showDash(user.Diet)} />
              <InfoRow label="Smoke" value={showDash(user.Smoke)} />
              <InfoRow label="Drink" value={showDash(user.Drink)} />
              <InfoRow label="Special Cases" value={showDash(user.spe_cases)} />
              <InfoRow label="Hobbies" value={showDash(user.Hobbies)} />
              <InfoRow label="Interests" value={showDash(user.Interests)} />
              <InfoRow label="Passport" value={showDash(user.passport)} />
            </div>
          </div>
        </section>

        {/* Family Details */}
        <section className="mb-6">
          <div className="bg-white dark:bg-[#221019] rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Family Details</h2>
              {isOwner && (
                <button
                  onClick={() => navigate("/profile/edit#family")}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <PencilSquareIcon className="w-5 h-5" />
                  Edit
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <InfoRow
                label="Family Values"
                value={showDash(user.Familyvalues)}
              />
              <InfoRow label="Family Type" value={showDash(user.FamilyType)} />
              <InfoRow
                label="Family Status"
                value={showDash(user.FamilyStatus)}
              />
              <InfoRow
                label="Number of Brothers"
                value={showDash(user.noofbrothers)}
              />
              <InfoRow
                label="Number of Sisters"
                value={showDash(user.noofsisters)}
              />

              <InfoRow
                label="Brothers Married"
                value={showDash(user.noyubrothers || user.nbm)}
              />

              <InfoRow
                label="Sisters Married"
                value={showDash(user.noyusisters || user.nsm)}
              />

              <InfoRow label="Father Name" value={showDash(user.Fathername)} />

              <InfoRow
                label="Father Occupation"
                value={showDash(user.Fathersoccupation)}
              />

              <InfoRow
                label="Father Poorvegam"
                value={showDash(user.FatherPoorvegam)}
              />

              <InfoRow label="Mother Name" value={showDash(user.Mothersname)} />

              <InfoRow
                label="Mother Occupation"
                value={showDash(user.Mothersoccupation)}
              />

              <InfoRow
                label="Mother Poorvegam"
                value={showDash(user.MotherPoorvegam)}
              />

              <InfoRow
                label="Family Wealth"
                value={showDash(user.family_wealth)}
              />

              <InfoRow
                label="Mother Tongue"
                value={showDash(user.mother_tounge || user.Language)}
              />

              <InfoRow
                label="Family Medical History"
                value={showDash(user.familymedicalhistory)}
              />

              <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                <span className="text-sm text-gray-500">Describe your family background and assets</span>
                <div className="font-medium">
                  {showDash(user.FamilyDetails || user.FamilyDetails_new)}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Partner Preference */}

        {/* ------------------ COMPATIBILITY SECTION ------------------ */}
        <section className="mb-12">
          <div className="bg-white dark:bg-[#221019] rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            {/* PHOTOS + SCORE */}
            <div className="flex items-center justify-between px-4 py-6">
              {/* Logged-in User */}
              <div className="flex flex-col items-center">
                <img
                  src={viewer?.PhotoURL || `${API}/gallery/nophoto.jpg`}
                  className="w-20 h-20 rounded-full border"
                  alt="You"
                />
                <span className="mt-2 font-semibold">{viewer?.Name}</span>
              </div>

              {/* SCORE IN CENTER */}
              <div className="text-center">
                <div className="text-4xl font-extrabold text-pink-600">
                  {score} / {total}
                </div>
                <div className="text-sm text-gray-500">Compatibility Score</div>
              </div>

              {/* Partner */}
              <div className="flex flex-col items-center">
                <img
                  src={profile?.PhotoURL || `${API}/gallery/nophoto.jpg`}
                  className="w-20 h-20 rounded-full border"
                  alt="Partner"
                />
                <span className="mt-2 font-semibold">{profile?.Name}</span>
              </div>
            </div>

            {/* BOTH SIDES */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              {/* LEFT COLUMN – YOUR PREFERENCE */}
              <div>
                <h3 className="font-semibold mb-2 text-pink-700">
                  Your Preference
                </h3>
                {compatibility.map((row, i) => (
                  <div key={i} className="flex items-center gap-2 py-1">
                    <span className="text-yellow-600 text-xl">{row.icon}</span>
                    <span className="text-gray-600 w-40">{row.label}:</span>
                    <span className="font-medium">{row.yourValue || "-"}</span>
                    <span
                      className={`ml-auto text-xl ${
                        row.pass ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {row.pass ? "✔️" : "❌"}
                    </span>
                  </div>
                ))}
              </div>

              {/* RIGHT COLUMN – PARTNER VALUES */}
              <div>
                <h3 className="font-semibold mb-2 text-pink-700">
                  Partner Details
                </h3>
                {compatibility.map((row, i) => (
                  <div key={i} className="flex items-center gap-2 py-1">
                    <span className="text-yellow-600 text-xl">{row.icon}</span>
                    <span className="text-gray-600 w-40">{row.label}:</span>
                    <span className="font-medium">
                      {row.partnerValue || "-"}
                    </span>
                    <span
                      className={`ml-auto text-xl ${
                        row.pass ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {row.pass ? "✔️" : "❌"}
                    </span>
                  </div>
                ))}
              </div>
              {showImagePreview && (
                <div
                  className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
                  onClick={() => setShowImagePreview(false)}
                >
                  <div
                    className="relative max-w-3xl w-full px-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Close Button */}
                    <button
                      onClick={() => setShowImagePreview(false)}
                      className="absolute -top-10 right-2 text-white text-3xl hover:opacity-80"
                      aria-label="Close"
                    >
                      ×
                    </button>

                    {/* Image */}
                    {/* Image with Watermark */}
                    <div className="relative rounded-xl overflow-hidden shadow-2xl">
                      <img
                        src={
                          photoSrc(user) && photoSrc(user) !== ""
                            ? photoSrc(user)
                            : "/nophoto.jpg"
                        }
                        alt="Profile"
                        className="w-full max-h-[80vh] object-contain bg-black"
                      />

                      {/* 🔒 Watermark Overlay */}
                      <div className="absolute inset-0 pointer-events-none">
                        {/* Diagonal lines */}
                        <div
                          className="absolute inset-0 opacity-20"
                          style={{
                            backgroundImage: `
          repeating-linear-gradient(
            -45deg,
            rgba(255,255,255,0.25),
            rgba(255,255,255,0.25) 1px,
            transparent 1px,
            transparent 120px
          )
        `,
                          }}
                        />

                        {/* Watermark Text */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-white text-3xl md:text-4xl font-bold tracking-widest rotate-[-25deg] opacity-30 select-none">
                            Sri Angalamman Matrimony
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );


  
}


function SouthChart({ title, data }) {
  return (
    <div className="inline-block my-6">
      <div className="grid grid-cols-4 grid-rows-4 w-[300px] h-[300px] border border-black">
        {/* ROW 1 */}
        <Cell value={data?.[0]} />
        <Cell value={data?.[1]} />
        <Cell value={data?.[2]} />
        <Cell value={data?.[3]} />

        {/* ROW 2 */}
        <Cell value={data?.[11]} />
        <div className="col-span-2 row-span-2 border border-black flex items-center justify-center font-bold text-lg">
          {title}
        </div>
        <Cell value={data?.[4]} />

        {/* ROW 3 */}
        <Cell value={data?.[10]} />
        <Cell value={data?.[5]} />

        {/* ROW 4 */}
        <Cell value={data?.[9]} />
        <Cell value={data?.[8]} />
        <Cell value={data?.[7]} />
        <Cell value={data?.[6]} />
      </div>
    </div>
  );
}

function Cell({ value }) {
  if (!value) return <div className="border border-black p-1 text-[12px]" />;

  let items = [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) items = parsed;
  } catch {
    items = value.split(",").map((v) => v.trim());
  }

  return (
    <div className="border border-black p-1 text-[11px] leading-tight">
      {items.map((item, i) => (
        <div key={i}>{item}</div>
      ))}
    </div>
  );
}
